import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { alertActions } from "../store/alertSlice";
import "../styles/alert.css";
import { LanguageContext } from "../context/language/LanguageContext";
import appConst from "../Utilities/appConst.json";

// Guard against non-string values (e.g. an Error object from a failed request)
const toText = (value) =>
  value == null || typeof value === "string"
    ? value
    : value?.message ?? String(value);

const DialogFrame = ({
  open,
  title,
  message,
  direction,
  onClose,
  actions,
  children,
}) => {
  if (!open) return null;

  return (
    <div className="dialog_overlay" role="presentation" onMouseDown={onClose}>
      <div
        className="dialog_panel dialog"
        role="dialog"
        aria-modal="true"
        dir={direction}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="dialog_title">{toText(title)}</h2>
        <div className="dialog_content">
          <p className="dialog_description">{toText(message)}</p>
          {children}
        </div>
        <div className="dialog_actions">{actions}</div>
      </div>
    </div>
  );
};

const Alert = () => {
  const dispatch = useDispatch();
  const languageContext = useContext(LanguageContext);
  const alert = useSelector((state) => state.alert);
  const open = useSelector((state) => state.alert.open);
  const direction = useSelector((state) => state.mode.direction);

  const [nextDialogOpen, setNextDialogOpen] = useState(false);
  const [value, setValue] = useState("");
  const [isError, setIsError] = useState(alert.isError);
  const [helperText, setHelperText] = useState(alert.helperText);

  useEffect(() => {
    setIsError(alert.isError);
  }, [alert.isError]);

  useEffect(() => {
    setHelperText(alert.helperText);
  }, [alert.helperText]);

  useEffect(() => {
    if (!open && !nextDialogOpen) return;

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (nextDialogOpen) {
        setNextDialogOpen(false);
      } else {
        handleClose(appConst.NO);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, nextDialogOpen]);

  const handleClose = (type) => {
    dispatch(alertActions.closeAlert(type));
  };

  const handleSubmit = (inputValue) => {
    dispatch(alertActions.submitInput(inputValue));
  };

  const handleNextDialogOpen = () => {
    setNextDialogOpen(true);
  };

  const handleNextDialogClose = () => {
    setNextDialogOpen(false);
  };

  const onUpdateField = (event) => {
    const nextValue = event.target.value || "";
    setValue(nextValue);
    setIsError(false);
    setHelperText("");

    dispatch(
      alertActions.setIsError({
        isValid: true,
        errorMessage: "",
      })
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit(value);
    }
  };

  const dialogActions = alert.isDialog ? (
    <>
      <button
        className="dialog-action-btn border-0 bg-transparent text-brand"
        onClick={() => {
          if (alert.hasNext) {
            handleNextDialogOpen();
          }

          if (alert.isInput) {
            handleSubmit(value);
          } else {
            handleClose(appConst.YES);
          }
        }}
        autoFocus
        type="button"
      >
        {languageContext.getTranslation(alert.yesButtonText)}
      </button>
      <button
        className="dialog-action-btn border-0 bg-transparent text-ink"
        onClick={() => {
          handleClose(appConst.NO);
        }}
        type="button"
      >
        {languageContext.getTranslation(alert.noButtonText)}
      </button>
    </>
  ) : (
    <button
      className="dialog-action-btn border-0 bg-transparent text-brand"
      onClick={() => {
        if (alert.hasScrollTo) {
          dispatch(alertActions.setShouldScrollTo(true));
        }
        handleClose(appConst.OK);
      }}
      type="button"
    >
      {languageContext.getTranslation(alert.okButtonText)}
    </button>
  );

  return (
    <>
      <DialogFrame
        open={open}
        title={alert.title}
        message={alert.message}
        direction={direction}
        onClose={() => handleClose(appConst.NO)}
        actions={dialogActions}
      >
        {alert.isInput && (
          <div className="alert_input">
            <input
              id="alert-coupon-input"
              autoFocus
              type="text"
              value={value}
              onChange={onUpdateField}
              onKeyDown={handleKeyDown}
              dir={direction}
              placeholder={languageContext.getTranslation(appConst.COUPON_CODE)}
              aria-label={languageContext.getTranslation(appConst.COUPON_CODE)}
              className={`alert_input_control ${isError ? "alert_input_error" : ""}`}
            />
            {!!helperText && (
              <p className="warning_font warning_color alert_input_helper">
                {toText(helperText)}
              </p>
            )}
          </div>
        )}
      </DialogFrame>

      <DialogFrame
        open={nextDialogOpen}
        title={languageContext.getTranslation(alert.nextDialogTitle)}
        message={languageContext.getTranslation(alert.nextDialogMessage)}
        direction={direction}
        onClose={handleNextDialogClose}
        actions={
          <button
            type="button"
            className="dialog-action-btn border-0 bg-transparent text-ink"
            onClick={handleNextDialogClose}
          >
            {languageContext.getTranslation(alert.nextDialogOkButtonText)}
          </button>
        }
      />
    </>
  );
};

export default Alert;
